import React from 'react';
import { Navigate, useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import ProjectList from './ProjectList';
import ProjectDetail from './ProjectDetail';
import ProjectGantt from './ProjectGantt';
import { ProjectViewMode } from '@/types/project';

interface ViewTabProps {
  id: ProjectViewMode;
  labelKey: string;
  icon: React.ReactNode;
}

const viewTabsConfig: ViewTabProps[] = [
  {
    id: 'detail',
    labelKey: 'views.detail',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
      </svg>
    ),
  },
  {
    id: 'gantt',
    labelKey: 'views.gantt',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
  },
];

const ProjectContainer: React.FC = () => {
  const { t } = useTranslation('project');
  const { isAuthenticated, isLoading } = useAuth();
  const { projectId, view, tab } = useParams<{ projectId?: string; view?: string; tab?: string }>();
  const navigate = useNavigate();

  // Default to 'list' if no view specified
  // For TTQS routes (/app/projects/:projectId/ttqs/:tab), view will be 'ttqs'
  const activeView = (view as ProjectViewMode) || 'list';

  // Wait for auth to load before redirecting
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  // Redirect to home if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/app" replace />;
  }

  // Redirect to /app/projects/list if accessing /app/projects without a view
  if (!view && !projectId) {
    return <Navigate to="/app/projects/list" replace />;
  }

  const handleViewChange = (viewId: ProjectViewMode) => {
    if (projectId) {
      navigate(`/app/projects/${projectId}/${viewId}`);
    } else {
      navigate(`/app/projects/${viewId}`);
    }
  };

  const handleSelectProject = (id: string) => {
    navigate(`/app/projects/${id}/detail`);
  };

  const handleBackToList = () => {
    navigate('/app/projects/list');
  };

  const renderContent = () => {
    // If we have a project ID, show project detail/views
    if (projectId) {
      switch (activeView) {
        case 'detail':
          return <ProjectDetail projectId={projectId} onBack={handleBackToList} />;
        case 'gantt':
          return <ProjectGantt projectId={projectId} />;
        default:
          return <ProjectDetail projectId={projectId} onBack={handleBackToList} />;
      }
    }

    // No project selected, show list
    return <ProjectList onSelectProject={handleSelectProject} />;
  };

  // Show view tabs only when a project is selected
  const showViewTabs = !!projectId;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md dark:shadow-gray-700 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="p-3 bg-emerald-100 dark:bg-emerald-900 rounded-full">
              <svg className="w-6 h-6 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
              </svg>
            </div>
            <div className="ml-4">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t('title')}</h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">{t('subtitle')}</p>
            </div>
          </div>
          {projectId && (
            <button
              onClick={handleBackToList}
              className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              {t('backToList')}
            </button>
          )}
        </div>
      </div>

      {/* View Navigation (only shown when a project is selected) */}
      {showViewTabs && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md dark:shadow-gray-700">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="flex -mb-px overflow-x-auto">
              {viewTabsConfig.map((tabItem) => (
                <button
                  key={tabItem.id}
                  onClick={() => handleViewChange(tabItem.id)}
                  className={`flex items-center px-6 py-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    activeView === tabItem.id
                      ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <span className={activeView === tabItem.id ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-400 dark:text-gray-500'}>
                    {tabItem.icon}
                  </span>
                  <span className="ml-2">{t(tabItem.labelKey)}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* View Content */}
          <div className="p-6">
            {renderContent()}
          </div>
        </div>
      )}

      {/* List Content (when no project selected) */}
      {!showViewTabs && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md dark:shadow-gray-700 p-6">
          {renderContent()}
        </div>
      )}
    </div>
  );
};

export default ProjectContainer;
